import React, { useEffect, useState } from 'react';
import { InternalFieldName, useForm } from 'react-hook-form';
import { CgProfile } from 'react-icons/cg';
import { toast } from 'react-toastify';
import Editor from 'react-simple-wysiwyg';
import { FaPlus } from 'react-icons/fa';
import CustomButton from '../CustomButton/CustomButton';
import Card from '../Card/Card';
import api from '../../services/api';
import { useAuth } from '../../contexts/auth';
import selectFile from '../../services/fileService';

export interface CompanyMailFormFields {
  emailSubject?: string;
  emailBody?: string;
  emailSignature?: string;
}

interface CompanyEmailFormProps {
  id: number;
  isLoading: boolean;
  formFields: CompanyMailFormFields;
  setFormFields: React.Dispatch<React.SetStateAction<CompanyMailFormFields>>;
}

function addServerErrors<T>(
  errors: { [P in keyof T]?: string[] },
  setError: any
) {
  return Object.keys(errors).forEach((key) => {
    setError(key as keyof T, {
      type: 'server',
      message: errors[key as keyof T]!.join('. '),
    });
  });
}

export default function CompanyEmailForm({
  id,
  isLoading,
  formFields,
  setFormFields,
}: CompanyEmailFormProps) {
  const {
    user: { organization },
  } = useAuth();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
    reset,
    getValues,
    setValue,
  } = useForm<CompanyMailFormFields>({ mode: 'onBlur' });

  const [logo, setLogo] = useState('');
  const [isSinagureImageLoading, setIsSinagureImageLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [htmlBody, setHtmlBody] = useState(formFields?.emailBody);
  const [htmlSignature, setHtmlSignature] = useState(
    formFields?.emailSignature
  );

  const onChangeBody = (e: any) => {
    setHtmlBody(e.target.value);
  };

  const onChangeSignature = (e: any) => {
    setHtmlSignature(e.target.value);
  };

  useEffect(() => {
    let shouldFocus = true;
    Object.keys(formErrors || {}).forEach((field: InternalFieldName) => {
      addServerErrors(formErrors, setError);

      shouldFocus = false;
    });
  }, [formErrors]);

  const onSubmit = async (company: CompanyMailFormFields) => {
    try {
      const { data } = await api.post(
        `organizations/${organization.id}/companies/${id}/email`,
        {
          emailSubject: company.emailSubject,
          emailBody: htmlBody,
          emailSignature: htmlSignature,
        }
      );

      setFormFields({
        emailSubject: data.emailSubject,
        emailBody: data.emailBody,
        emailSignature: data.emailSignature,
      });

      toast.success('Dados de e-mail configurados com sucesso');
    } catch (e: any) {
      if (e.response.data.errors) {
        setFormErrors(e.response.data.errors);
      }
    }
  };

  const getSignatureImage = async () => {
    try {
      setIsSinagureImageLoading(true);
      const { data } = await api.get(
        `organizations/${organization.id}/companies/${id}/signature-image`
      );
      setLogo(data);
    } catch (e: any) {
      if (e?.response?.status === 404) {
        toast.info('A imagem de assinatura ainda não foi cadastrada');
      }
    } finally {
      setIsSinagureImageLoading(false);
    }
  };

  const addFile = async () => {
    try {
      const files = await selectFile(['image/png', 'image/jpeg'], false);
      if (files && files?.length > 0) {
        toast.info('Aguarde o upload da imagem de assinatura');
        await api.postForm(
          `organizations/${organization.id}/companies/${id}/signature-image`,
          { 'signature-image': files[0] }
        );
        toast.success('Imagem de assinatura salvo com sucesso');
        getSignatureImage();
      }
    } catch (e: any) {
      if (e?.response?.data?.errors) {
        if (e?.response?.status === 422) {
          setFormErrors(e.response.data.errors);
        } else {
          toast.error(
            e?.response?.data?.errors.reduce(
              (acc: string, error: string) => acc + error,
              ''
            )
          );
        }
      } else {
        throw e;
      }
    }
  };

  const removeFile = async () => {
    try {
      if (
        // eslint-disable-next-line no-restricted-globals
        confirm(
          'Você deseja remover a imagem da assinatura do e-mail? Essa ação não poderá ser desfeita'
        )
      ) {
        await api.delete(
          `organizations/${organization.id}/companies/${id}/signature-image`
        );

        toast.success('Imagem da assinatura removida com sucesso');
        setLogo('');
      }
    } catch (e: any) {
      if (e?.response?.data?.errors) {
        if (e?.response?.status === 422) {
          setFormErrors(e.response.data.errors);
        } else {
          toast.error(
            e?.response?.data?.errors.reduce(
              (acc: string, error: string) => acc + error,
              ''
            )
          );
        }
      } else {
        throw e;
      }
    }
  };

  function addSubjectVar(subjectVar: string): void {
    const emailSubject = getValues('emailSubject');
    setValue('emailSubject', `${emailSubject ?? ''} ${subjectVar}`.trim());
  }

  useEffect(() => {
    reset(formFields);
    getSignatureImage();
  }, [formFields]);

  return (
    <form>
      <div className='mb-3'>
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label htmlFor='inputLabel' className='form-label'>
          Título
        </label>
        <input
          {...register('emailSubject', { required: true })}
          className='form-control'
          placeholder='Título'
          type='text'
          required
        />
        <div className='d-flex gap-2 mt-2'>
          <button
            type='button'
            className='btn btn-outline-secondary btn-sm d-flex align-items-center gap-1'
            onClick={() => addSubjectVar('{{numero}}')}
          >
            <FaPlus /> Número do Orçamento
          </button>
          <button
            type='button'
            className='btn btn-outline-secondary btn-sm d-flex align-items-center gap-1'
            onClick={() => addSubjectVar('{{cliente}}')}
          >
            <FaPlus /> Cliente
          </button>
        </div>
        {errors.emailSubject && (
          <div className='form-text text-danger'>
            {errors.emailSubject.message}
          </div>
        )}
      </div>
      <div className='mb-3'>
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label htmlFor='inputLabel' className='form-label'>
          Mensagem
        </label>
        <Editor
          placeholder='Mensagem'
          value={htmlBody}
          onChange={onChangeBody}
        />
        {errors.emailBody && (
          <div className='form-text text-danger'>
            {errors.emailBody.message}
          </div>
        )}
      </div>
      <div className='mb-3'>
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label htmlFor='inputLabel' className='form-label'>
          Assinatura
        </label>
        <Editor
          placeholder='Assinatura'
          value={htmlSignature}
          onChange={onChangeSignature}
        />
        {errors.emailSignature && (
          <div className='form-text text-danger'>
            {errors.emailSignature.message}
          </div>
        )}
      </div>
      <Card header='Imagem da assinatura' isLoading={isSinagureImageLoading}>
        {logo && !isSinagureImageLoading && (
          <img width={300} src={logo} alt='Imagem da assinatura' />
        )}
        <div className='mt-3 d-flex gap-2'>
          <CustomButton
            type='button'
            className='btn btn-primary'
            handleClick={() => addFile()}
          >
            Adicionar <CgProfile />
          </CustomButton>

          <CustomButton
            className='btn btn-danger'
            type='button'
            handleClick={() => removeFile()}
          >
            Remover
          </CustomButton>
        </div>
      </Card>

      <div className='d-flex mt-3 mb-3 justify-content-end gap-2'>
        <CustomButton
          type='button'
          className='btn btn-primary'
          handleClick={handleSubmit(onSubmit)}
        >
          Salvar
        </CustomButton>
      </div>
    </form>
  );
}
